import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MdForum } from "react-icons/md";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Card from "components/card";
import DataTable from "components/data-table";
import Tabs from "components/tabs";
import { EmptyState } from "components/states";
import { EMPTY, formatDate } from "utils/format";
import CardError from "./CardError";

const TABS = [
  { id: "staff", label: "Staff" },
  { id: "volunteer", label: "Volunteer" },
  { id: "heartbeat", label: "Heartbeat" },
];

const SENTIMENTS = {
  very_positive: { label: "Very Positive", tone: "success" },
  positive: { label: "Positive", tone: "success" },
  neutral: { label: "Neutral", tone: "neutral" },
  negative: { label: "Negative", tone: "danger" },
  very_negative: { label: "Very Negative", tone: "danger" },
};

const getScoreColor = (score) => {
  if (score >= 4) return "text-success-fg";
  if (score >= 3) return "text-warning-fg";
  return "text-danger-fg";
};

const getScoreLabel = (score) => {
  if (score >= 4.5) return "Excellent";
  if (score >= 4) return "Good";
  if (score >= 3) return "Fair";
  if (score >= 2) return "Low";
  return "Critical";
};

const ScoreCell = ({ score }) => {
  if (score == null) return <span className="text-fg-muted">{EMPTY}</span>;
  return (
    <span className={`font-semibold ${getScoreColor(score)}`}>
      {score}/5
      <span className="ml-1 text-xs font-normal text-fg-muted">· {getScoreLabel(score)}</span>
    </span>
  );
};

const nameColumn = {
  id: "name",
  header: "Name",
  cell: ({ row }) => (
    <div className="flex items-center gap-3">
      <Avatar src={row.original.avatar} name={row.original.userName || "?"} size="sm" noBorder />
      <span className="whitespace-nowrap font-medium text-fg">{row.original.userName}</span>
    </div>
  ),
};

const dateColumn = {
  id: "date",
  header: "Date",
  cell: ({ row }) => <span className="text-fg-muted">{formatDate(row.original.submittedAt)}</span>,
};

const HEARTBEAT_COLUMNS = [
  nameColumn,
  {
    id: "survey",
    header: "Survey",
    cell: ({ row }) => <span className="text-fg-muted">{row.original.campaignName || EMPTY}</span>,
  },
  {
    id: "score",
    header: "Score",
    cell: ({ row }) => (
      <span className="font-semibold text-fg">
        {row.original.overallScore != null ? row.original.overallScore.toFixed(1) : EMPTY}
      </span>
    ),
  },
  dateColumn,
];

const PULSE_COLUMNS = [
  nameColumn,
  {
    id: "satisfaction",
    header: "Satisfaction",
    cell: ({ row }) => <ScoreCell score={row.original.satisfactionScore} />,
  },
  {
    id: "contribution",
    header: "Contribution",
    cell: ({ row }) => <ScoreCell score={row.original.contributionScore} />,
  },
  {
    id: "sentiment",
    header: "Sentiment",
    cell: ({ row }) => {
      const key = row.original.sentimentLabel;
      if (!key) return <span className="text-fg-muted">{EMPTY}</span>;
      const sentiment = SENTIMENTS[key];
      return <Badge tone={sentiment?.tone || "neutral"}>{sentiment?.label || key}</Badge>;
    },
  },
  dateColumn,
];

const getRowId = (row, index) => String(row.id ?? index);

const ResponseTable = ({
  data,
  pagination,
  activeTab,
  onTabChange,
  onPageChange,
  loading,
  error,
  onRetry,
}) => {
  const navigate = useNavigate();
  const isHeartbeat = activeTab === "heartbeat";
  const columns = isHeartbeat ? HEARTBEAT_COLUMNS : PULSE_COLUMNS;

  const handleRowClick = (row) => {
    if (activeTab === "staff") {
      navigate(`/admin/staff/people/${row.userId}`);
    } else if (activeTab === "volunteer") {
      navigate(`/admin/volunteers/people/${row.userId}`);
    } else if (isHeartbeat) {
      navigate(`/admin/heartbeat/${row.campaignId}?responseId=${row.id}`);
    }
  };

  const tablePagination = useMemo(
    () =>
      pagination
        ? {
            pageIndex: pagination.page - 1,
            pageSize: pagination.per,
            total: pagination.totalCount,
            onPageChange: (index) => onPageChange(index + 1),
          }
        : undefined,
    [pagination, onPageChange]
  );

  return (
    <section aria-labelledby="recent-responses-heading" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <h2 id="recent-responses-heading" className="text-heading">
          Recent Responses
        </h2>
        <Tabs
          aria-label="Response type"
          tabs={TABS}
          value={activeTab}
          onChange={onTabChange}
        />
      </div>
      {error ? (
        <Card padding="md">
          <CardError title="Couldn't Load Responses" error={error} onRetry={onRetry} />
        </Card>
      ) : (
        <DataTable
          aria-label="Recent responses"
          columns={columns}
          data={data || undefined}
          loading={loading}
          getRowId={getRowId}
          onRowClick={handleRowClick}
          pagination={tablePagination}
          emptyState={
            <EmptyState
              icon={MdForum}
              title="No Responses Yet"
              description="Survey responses will appear here as people submit them."
            />
          }
        />
      )}
    </section>
  );
};

export default ResponseTable;
